import { useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { fromLocalInput, toLocalInput } from "./utils";
import { detectGpuPlatform, SERVER_SKU_CATALOG } from "./hardware";
import { ContactAvatarInput } from "./ContactAvatarInput";
import type { Lead } from "./types";

const selectClass =
  "h-9 w-full rounded-md border border-input bg-card px-2.5 text-sm shadow-none outline-none transition focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/20";

function Field({
  label,
  children,
  wide = false,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <label
      className={`flex min-w-0 flex-col gap-1.5 text-xs font-medium text-muted-foreground ${wide ? "sm:col-span-2" : ""}`}
    >
      {label}
      {children}
    </label>
  );
}

function TransactionStepsInput({
  value,
  onChange,
}: {
  value: Lead;
  onChange: (lead: Lead) => void;
}) {
  const [draft, setDraft] = useState(() => value.transaction_steps.join("\n"));
  const updateDraft = (nextDraft: string) => {
    setDraft(nextDraft);
    onChange({
      ...value,
      transaction_steps: nextDraft
        .split(/\r?\n/)
        .map((step) => step.trim())
        .filter(Boolean),
    });
  };

  return (
    <Textarea
      aria-label="交易流程（一行一步）"
      value={draft}
      onChange={(event) => updateDraft(event.target.value)}
      className="min-h-24 placeholder:text-foreground/35"
      placeholder={"验资（飘 1.2 USDT）\n现场验货\n付款（验一付一）"}
    />
  );
}

async function compressNoteImage(file: File): Promise<string> {
  const source = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("图片读取失败"));
    reader.readAsDataURL(file);
  });
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const element = new Image();
    element.onload = () => resolve(element);
    element.onerror = () => reject(new Error("图片格式不受支持"));
    element.src = source;
  });
  const scale = Math.min(
    1,
    1600 / Math.max(image.naturalWidth, image.naturalHeight),
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("当前浏览器无法处理图片");
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.82);
}

export function LeadFields({
  value,
  onChange,
  users,
}: {
  value: Lead;
  onChange: (lead: Lead) => void;
  users?: { id: string; username: string }[];
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const set = <K extends keyof Lead>(key: K, next: Lead[K]) =>
    onChange({ ...value, [key]: next });
  const numberValue = (raw: string) => (raw === "" ? null : Number(raw));
  const platform = detectGpuPlatform(value.model, value.raw_text, value.sku);
  const skuOptions = platform
    ? SERVER_SKU_CATALOG.filter((entry) => entry.platform.includes(platform))
    : [];
  const addNoteImages = async (files: File[]) => {
    const images = [...value.note_images];
    for (const file of files
      .filter((item) => item.type.startsWith("image/"))
      .slice(0, 6)) {
      images.push(await compressNoteImage(file));
    }
    if (images.length !== value.note_images.length) set("note_images", images);
  };

  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
      <Field label="方向">
        <select
          className={selectClass}
          value={value.direction}
          onChange={(e) =>
            set("direction", e.target.value as Lead["direction"])
          }
        >
          <option value="sell">出售</option>
          <option value="buy">求购</option>
        </select>
      </Field>
      <Field label="状态">
        <select
          className={selectClass}
          value={value.status}
          onChange={(e) => set("status", e.target.value as Lead["status"])}
        >
          <option value="draft">草稿</option>
          <option value="active">有效</option>
          <option value="locked">已锁定</option>
          <option value="done">已成交</option>
          <option value="closed">已关闭</option>
          <option value="archived">已归档</option>
        </select>
      </Field>
      <Field label="品类">
        <Input
          value={value.category}
          onChange={(e) => set("category", e.target.value)}
          placeholder="算力设备 / 有色金属"
        />
      </Field>
      <Field label="品牌">
        <Input
          value={value.brand}
          onChange={(e) => set("brand", e.target.value)}
          placeholder="超微 / 技嘉 / NVIDIA"
        />
      </Field>
      <Field label={platform ? "官方服务器 SKU" : "SKU"}>
        {platform ? (
          <select
            className={selectClass}
            value={value.sku}
            onChange={(e) => set("sku", e.target.value)}
          >
            <option value="">待确认（原文未提供）</option>
            {skuOptions.map((entry) => (
              <option key={`${entry.brand}-${entry.sku}`} value={entry.sku}>
                {entry.brand} · {entry.sku}
              </option>
            ))}
          </select>
        ) : (
          <Input
            value={value.sku}
            onChange={(e) => set("sku", e.target.value)}
            placeholder="其他品类的 SKU"
          />
        )}
      </Field>
      <Field label="内存容量">
        <Input
          value={value.memory_capacity}
          onChange={(e) => set("memory_capacity", e.target.value)}
          placeholder="例如：3T / 288GB"
        />
      </Field>
      <Field label={platform ? "平台 / 版本 *" : "型号规格 *"} wide>
        <Input
          required
          value={value.model}
          onChange={(e) => set("model", e.target.value)}
          placeholder={platform ? `例如：${platform} 3T` : "例如：电解铜 A 级"}
        />
      </Field>
      <Field label={platform ? "变量配置" : "交付形态 / 配置"} wide>
        <Input
          value={value.configuration}
          onChange={(e) => set("configuration", e.target.value)}
          placeholder={
            platform
              ? "仅填写风冷 / 液冷等交易差异，不重复固定硬件参数"
              : "整机 / 模组、风冷 / 液冷等"
          }
        />
      </Field>
      <Field label="数量">
        <Input
          type="number"
          min="0"
          step="any"
          value={value.quantity ?? ""}
          onChange={(e) => set("quantity", numberValue(e.target.value))}
        />
      </Field>
      <Field label="单位">
        <Input
          value={value.unit}
          onChange={(e) => set("unit", e.target.value)}
          placeholder="台 / 吨"
        />
      </Field>
      <Field label="单价（留空为面议）">
        <Input
          type="number"
          min="0"
          step="any"
          value={value.price ?? ""}
          onChange={(e) => set("price", numberValue(e.target.value))}
          placeholder="万"
          className="unit-price-placeholder"
        />
      </Field>
      <Field label="币种">
        <select
          className={selectClass}
          value={value.currency}
          onChange={(e) => set("currency", e.target.value)}
        >
          <option value="CNY">CNY 人民币</option>
          <option value="USD">USD 美金</option>
          <option value="USDT">USDT</option>
        </select>
      </Field>
      <Field label="预估居间费 / 台" wide>
        <Input
          type="number"
          min="0"
          step="any"
          value={value.estimated_fee_per_unit ?? ""}
          onChange={(e) =>
            set("estimated_fee_per_unit", numberValue(e.target.value))
          }
          placeholder="与报价同一币种，留空为待确认"
        />
      </Field>
      <Field label="价格口径">
        <Input
          value={value.price_basis}
          onChange={(e) => set("price_basis", e.target.value)}
          placeholder="含税 / 未税 / 自提"
        />
      </Field>
      <Field label="库存状态">
        <select
          className={selectClass}
          value={value.availability}
          onChange={(e) =>
            set("availability", e.target.value as Lead["availability"])
          }
        >
          <option value="stock">现货</option>
          <option value="transit">在途</option>
          <option value="presale">预售</option>
          <option value="unknown">待确认</option>
        </select>
      </Field>
      <Field label="交割地">
        <Input
          value={value.location}
          onChange={(e) => set("location", e.target.value)}
          placeholder="城市 / 仓库"
        />
      </Field>
      <Field label="可交割时间">
        <Input
          type="datetime-local"
          value={toLocalInput(value.available_at)}
          onChange={(e) => set("available_at", fromLocalInput(e.target.value))}
        />
      </Field>
      <Field label="有效至 *">
        <Input
          required
          type="datetime-local"
          value={toLocalInput(value.valid_until)}
          onChange={(e) =>
            set("valid_until", fromLocalInput(e.target.value) ?? "")
          }
        />
      </Field>
      <Field label="最近核验 *">
        <Input
          required
          type="datetime-local"
          value={toLocalInput(value.last_verified_at)}
          onChange={(e) =>
            set("last_verified_at", fromLocalInput(e.target.value) ?? "")
          }
        />
      </Field>
      <Field label="付款 / 交付条件" wide>
        <Input
          value={value.payment_terms}
          onChange={(e) => set("payment_terms", e.target.value)}
          placeholder="仅记录，不执行支付"
        />
      </Field>
      <Field label="验货 / 凭证要求" wide>
        <Input
          value={value.inspection}
          onChange={(e) => set("inspection", e.target.value)}
          placeholder="SN、仓单、压测等"
        />
      </Field>
      <Field label="交易流程（一行一步）" wide>
        <TransactionStepsInput
          key={value.id}
          value={value}
          onChange={onChange}
        />
      </Field>
      <ContactAvatarInput
        value={value.contact_avatar}
        onChange={(avatar) => set("contact_avatar", avatar)}
      />
      <Field label="公司">
        <Input
          value={value.company}
          onChange={(e) => set("company", e.target.value)}
        />
      </Field>
      <Field label="联系人">
        <Input
          value={value.contact_name}
          onChange={(e) => set("contact_name", e.target.value)}
        />
      </Field>
      <Field label="联系方式">
        <Input
          value={value.contact_info}
          onChange={(e) => set("contact_info", e.target.value)}
        />
      </Field>
      <Field label="来源渠道">
        <Input
          value={value.source_channel}
          onChange={(e) => set("source_channel", e.target.value)}
          placeholder="微信 / 邮件 / 电话"
        />
      </Field>
      <Field label="负责人">
        {users && users.length > 0 ? (
          <select
            className={selectClass}
            aria-label="负责人"
            value={
              users.some((user) => user.id === value.owner) ? value.owner : ""
            }
            onChange={(e) => set("owner", e.target.value)}
          >
            {!users.some((user) => user.id === value.owner) && (
              <option value="">
                {value.owner
                  ? `未知（${value.owner.slice(0, 8)}）`
                  : "选择负责人"}
              </option>
            )}
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.username}
              </option>
            ))}
          </select>
        ) : (
          <Input
            value={value.owner}
            onChange={(e) => set("owner", e.target.value)}
          />
        )}
      </Field>
      <div className="min-w-0 sm:col-span-2">
        <div className="mb-1.5 flex items-center justify-between gap-3">
          <label
            htmlFor="lead-internal-notes"
            className="text-xs font-medium text-muted-foreground"
          >
            内部备注
          </label>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(event) => {
              void addNoteImages(Array.from(event.target.files ?? []));
              event.target.value = "";
            }}
          />
          <button
            type="button"
            aria-label="上传备注图片"
            className="inline-flex min-h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
            onClick={() => fileInput.current?.click()}
          >
            <ImagePlus className="size-3.5" />
            添加图片
          </button>
        </div>
        <Textarea
          id="lead-internal-notes"
          aria-label="内部备注"
          value={value.notes}
          onChange={(e) => set("notes", e.target.value)}
          onPaste={(event) => {
            const files = Array.from(event.clipboardData.files).filter((file) =>
              file.type.startsWith("image/"),
            );
            if (files.length) {
              event.preventDefault();
              void addNoteImages(files);
            }
          }}
          className="min-h-24"
          placeholder="输入备注，或直接粘贴截图"
        />
        {value.note_images.length > 0 && (
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {value.note_images.map((image, index) => (
              <div
                key={`${index}-${image.slice(-24)}`}
                className="group relative overflow-hidden rounded-md border bg-muted/30"
              >
                <img
                  src={image}
                  alt={`备注图片 ${index + 1}`}
                  className="aspect-[4/3] w-full object-cover"
                />
                <button
                  type="button"
                  aria-label={`删除备注图片 ${index + 1}`}
                  className="absolute top-2 right-2 grid size-8 place-items-center rounded-md bg-background/95 text-foreground shadow-sm transition hover:bg-destructive hover:text-white"
                  onClick={() =>
                    set(
                      "note_images",
                      value.note_images.filter(
                        (_, itemIndex) => itemIndex !== index,
                      ),
                    )
                  }
                >
                  <X className="size-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <Field label="原始消息" wide>
        <Textarea
          value={value.raw_text}
          onChange={(e) => set("raw_text", e.target.value)}
          className="min-h-24"
          placeholder="保留原文，方便核对 AI 提取结果"
        />
      </Field>
    </div>
  );
}
